import { createSelector } from 'reselect';
import { RetirementIncomeBreakdownBarChart } from '../../js/app/react/chartDescriptors.financialPlan';
import { getSelectedGoalChart } from '../FinancialPlan/Retirement/RetirementSelector';
import { RETIREMENT_INCOME } from '../FinancialPlan/Retirement/RetirementConstants';
import { selectedGoal } from '../FinancialPlanGoalDropdown/FinancialPlanGoalDropdownSelector';
import { setExpanded, setIndex } from './RetirementIncomeChartActions';
import { selectIsCanadian } from '../CommonSelectors/CommonAreaSelector';

let chartData,
  categories,
  annuity,
  distributions,
  guaranteedIncomeShortage,
  incomeShortage,
  otherIncome,
  pension,
  retirementExpenses,
  socialSecurity,
  totalGuaranteedIncome,
  totalIncome,
  localProps,
  localDescriptor,
  initializeSets = () => {
    categories = [];
    annuity = [];
    distributions = [];
    guaranteedIncomeShortage = [];
    incomeShortage = [];
    otherIncome = [];
    pension = [];
    retirementExpenses = [];
    socialSecurity = [];
    totalGuaranteedIncome = [];
    totalIncome = [];
  },
  parseData = Data => {
    initializeSets();

    Data.forEach(function(item) {
      categories.push(item.Category);
      annuity.push(item.Annuity);
      distributions.push(item.Distributions);
      guaranteedIncomeShortage.push(Math.abs(item.GuaranteedIncomeShortage));
      incomeShortage.push(Math.abs(item.IncomeShortage));
      otherIncome.push(item.OtherIncome);
      pension.push(item.Pension);
      retirementExpenses.push(item.RetirementExpenses);
      socialSecurity.push(item.SocialSecurity);
      totalGuaranteedIncome.push(item.TotalGuaranteedIncome);
      totalIncome.push(item.TotalIncome);
    });

    chartData = {
      Categories: categories,
      Series: [
        {
          name: 'Distributions',
          data: distributions,
          index: 3,
        },
        {
          name: 'Other',
          data: otherIncome,
          index: 2,
        },
        {
          name: 'Annuity',
          data: annuity,
          index: 4,
        },
        {
          name: 'Pension',
          data: pension,
          index: 5,
        },
        {
          name: 'Social Security',
          data: socialSecurity,
          index: 6,
        },
        {
          name: 'Monthly Expenses',
          data: retirementExpenses,
          index: 1,
          type: 'spline',
          dashStyle: 'dash',
          marker: {
            enabled: false,
          },
        },
      ],
    };
  },
  buildDetail = (index, categories, isCanadian) => {
    var breakdown = [
      ...(!isCanadian
        ? [
            {
              name: 'Social Security',
              classes: 'icon ss',
              amount: '$' + ADV.util.number_format(socialSecurity[index], 0),
            },
          ]
        : []),
      {
        name: 'Pension',
        classes: 'icon p',
        amount: '$' + ADV.util.number_format(pension[index], 0),
      },
      {
        name: 'Annuities',
        classes: 'icon a',
        amount: '$' + ADV.util.number_format(annuity[index], 0),
      },
      {
        name: 'Total Guaranteed Income',
        classes: 'tgi',
        amount: '$' + ADV.util.number_format(totalGuaranteedIncome[index], 0),
      },
      {
        name: 'Monthly Expenses',
        classes: 'icon me',
        amount: '$' + ADV.util.number_format(retirementExpenses[index], 0),
      },
      {
        name: 'Guaranteed Income Shortage',
        classes: 'gis',
        amount: '$' + ADV.util.number_format(guaranteedIncomeShortage[index], 0),
      },
      {
        name: 'Distributions',
        classes: 'icon d',
        amount: '$' + ADV.util.number_format(distributions[index], 0),
      },
      {
        name: 'Other',
        classes: 'icon oi',
        amount: '$' + ADV.util.number_format(otherIncome[index], 0),
      },
      {
        name: 'Income Shortage',
        classes: 'icon is',
        amount: '$' + ADV.util.number_format(incomeShortage[index], 0),
      },
    ];
    return {
      year: categories[index],
      breakdown: breakdown,
    };
  };

export const viewStateSelector = (state, props) => {
    return state.RetirementIncomeChart[props.clientId] || {};
  },
  chartDataSelector = createSelector(
    [
      (state, props) => {
        const goalChart = getSelectedGoalChart(
          RETIREMENT_INCOME,
          props.goal || selectedGoal(state, props),
        );

        return {
          Data: goalChart && goalChart.Data,
          isPrint: props.isPrint,
          clientId: props.clientId,
        };
      },
    ],
    props => {
      if (ADV.util.shallowEqual(props, localProps)) {
        return localDescriptor;
      }
      const { Data, isPrint, clientId } = props;

      if (!Data) {
        return null;
      }

      var descriptor = clone(RetirementIncomeBreakdownBarChart);
      parseData(Data);

      descriptor.xAxis.categories = chartData.Categories;
      descriptor.series = chartData.Series;
      descriptor.series.forEach(function(item) {
        var selected_color =
          ADV.settings.retirement_income_colors[
            ADV.settings.retirement_income_colors.length - item.index
          ];
        item.color = selected_color;
        item.width = 4;
        item.border = 1;
        item.borderColor = selected_color;
      });

      descriptor.series.unshift({
        name: 'Total Income Shortage',
        data: incomeShortage,
        index: 0,
        color: '#f5f5f5',
        borderColor: '#d7d7d7',
        border: 1,
      });

      descriptor.plotOptions.series = {
        events: {
          click: function(e) {
            ADV.store.dispatch(setIndex(e.point.index, clientId));
            ADV.store.dispatch(setExpanded(true, clientId));
          },
        },
      };
      if (isPrint) {
        descriptor.plotOptions.column.animation = 0;
      }

      localProps = props;
      localDescriptor = descriptor;
      return descriptor;
    },
  ),
  detailSelector = (index, categories, isCanadian) => {
    return buildDetail(index, categories, isCanadian);
  },
  retirementIncomeChartSelector = (state, props) => {
    const viewState = viewStateSelector(state, props),
      chartData = chartDataSelector(state, props);

    const isCanadian = selectIsCanadian(state);

    let year = 0,
      index = viewState.index,
      firstYear = false,
      lastYear = false,
      categories = [],
      breakdownArray = [];

    if (chartData && chartData.xAxis) {
      categories = chartData.xAxis.categories;
    }

    if (!ADV.util.isNumeric(index)) {
      index = 0;
    } else {
      index = +index;
      if (index < 0) {
        index = 0;
      }

      if (index >= categories.length) {
        index = categories.length - 1;
      }
    }

    if (index == 0) {
      firstYear = true;
    }

    if (index == categories.length - 1) {
      lastYear = true;
    }

    if (categories.length > 0) {
      const detail = detailSelector(index, categories, isCanadian);
      year = parseInt(detail.year);
      breakdownArray = detail.breakdown;
    }
    return {
      chartData,
      expanded: !!viewState.expanded,
      index,
      firstYear,
      lastYear,
      showDetails: !!viewState.showDetails,
      breakdownArray,
      year,
      isCanadian,
    };
  };
